import { COLORS, LINE_NAMES, jarImage, withAlpha } from '../constants';
import { renderJarImages } from '../components/three/jarSnapshots';
import { JAR_TYPES } from '../components/three/jarMeta';
import { setLetterSpacing } from './canvasText';
import { logWarn } from './log';

export const SHARE_W = 1080;
export const SHARE_H = 1920;
const FONT = "'Comfortaa', 'Helvetica Neue', Arial, sans-serif";

const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const jarLabel = (p) => [JAR_TYPES[p.product_type]?.label[0] || '', (p.main_ingredient || '').toUpperCase()];

const loadJars = async (items) => {
  const rendered = await renderJarImages(items.map((p) => ({ color: COLORS[p.line_color] || '#ffffff', type: p.product_type, label: jarLabel(p) }))).catch((error) => { logWarn('3D jar render failed, using photo cutouts:', error); return []; });
  return Promise.all(items.map((p, i) => rendered[i] || loadImage(jarImage(p))));
};

const brandGradient = (ctx, x0, x1, y = 0) => {
  const g = ctx.createLinearGradient(x0, y, x1, y);
  g.addColorStop(0, COLORS.green);
  g.addColorStop(0.35, COLORS.pink);
  g.addColorStop(0.7, COLORS.orange);
  g.addColorStop(1, COLORS.blue);
  return g;
};

const orb = (ctx, x, y, r, color, alpha) => {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, withAlpha(color, alpha));
  g.addColorStop(1, withAlpha(color, 0));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, SHARE_W, SHARE_H);
};

const fitFont = (ctx, text, weight, start, min, maxWidth) => {
  let size = start;
  ctx.font = `${weight} ${size}px ${FONT}`;
  while (ctx.measureText(text).width > maxWidth && size > min) {
    size -= 2;
    ctx.font = `${weight} ${size}px ${FONT}`;
  }
  return size;
};

const pill = (ctx, text, cx, cy, size, color, pad = 34) => {
  ctx.font = `600 ${size}px ${FONT}`;
  const w = ctx.measureText(text).width + pad * 2;
  const h = size * 2.1;
  ctx.fillStyle = 'rgba(255,255,255,0.07)';
  ctx.strokeStyle = 'rgba(255,255,255,0.16)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(cx - w / 2, cy - h / 2, w, h, h / 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, cx, cy + 1);
};

const drawJarFallback = (ctx, cx, baseY, h, color) => {
  const w = h * 0.9;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(cx - w / 2, baseY - h * 0.72, w, h * 0.72, 22);
  ctx.fill();
  ctx.fillStyle = withAlpha('#ffffff', 0.18);
  ctx.beginPath();
  ctx.ellipse(cx, baseY - h * 0.72, w / 2, h * 0.16, 0, 0, Math.PI * 2);
  ctx.fill();
};

export const renderKitShareImage = async ({ name, fn, items, labels }) => {
  if (document.fonts?.load) {
    await Promise.all([`700 100px ${FONT}`, `600 36px ${FONT}`, `400 30px ${FONT}`].map((f) => document.fonts.load(f).catch((error) => logWarn('Font preload failed:', error))));
  }
  const canvas = document.createElement('canvas');
  canvas.width = SHARE_W;
  canvas.height = SHARE_H;
  const ctx = canvas.getContext('2d');
  const W = SHARE_W;
  const H = SHARE_H;

  ctx.fillStyle = '#08080b';
  ctx.fillRect(0, 0, W, H);
  orb(ctx, 140, 300, 680, COLORS.pink, 0.42);
  orb(ctx, 980, 1560, 760, COLORS.blue, 0.42);
  orb(ctx, 940, 560, 460, COLORS.orange, 0.2);
  orb(ctx, 120, 1780, 520, COLORS.green, 0.24);
  ctx.globalAlpha = 0.05;
  for (let i = 0; i < 9000; i++) {
    ctx.fillStyle = i % 2 ? '#ffffff' : '#000000';
    ctx.fillRect(Math.random() * W, Math.random() * H, 2, 2);
  }
  ctx.globalAlpha = 1;

  const [logo, jars] = await Promise.all([loadImage('/images/beautykit-logo.png'), loadJars(items)]);
  if (logo) {
    const lw = 440;
    const lh = (lw * logo.height) / logo.width;
    ctx.drawImage(logo, (W - lw) / 2, 110, lw, lh);
  }
  pill(ctx, labels.tagline, W / 2, 330, 26, 'rgba(255,255,255,0.75)');

  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur = 30;
  ctx.shadowOffsetY = 12;
  fitFont(ctx, name, 700, 104, 56, 940);
  ctx.fillText(name, W / 2, 500);
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  if (fn) {
    setLetterSpacing(ctx, '6px');
    fitFont(ctx, fn, 400, 50, 30, 940);
    const fw = ctx.measureText(fn).width;
    ctx.fillStyle = brandGradient(ctx, W / 2 - fw / 2, W / 2 + fw / 2);
    ctx.fillText(fn, W / 2, 585);
    setLetterSpacing(ctx, '0px');
  }

  const baseY = 1010;
  const centers = items.map((_, i) => W / 2 + (i - 2) * 210);
  const heights = [220, 260, 300, 260, 220];
  items.forEach((p, i) => {
    const color = COLORS[p.line_color] || '#fff';
    const g = ctx.createRadialGradient(centers[i], baseY - 20, 0, centers[i], baseY - 20, 190);
    g.addColorStop(0, withAlpha(color, 0.55));
    g.addColorStop(1, withAlpha(color, 0));
    ctx.fillStyle = g;
    ctx.fillRect(centers[i] - 200, baseY - 260, 400, 340);
  });
  [0, 4, 1, 3, 2].forEach((i) => {
    const img = jars[i];
    const h = heights[i];
    const p = items[i];
    ctx.shadowColor = 'rgba(0,0,0,0.65)';
    ctx.shadowBlur = 40;
    ctx.shadowOffsetY = 24;
    if (img) {
      const w = (h * img.width) / img.height;
      ctx.drawImage(img, centers[i] - w / 2, baseY - h + (i === 2 ? 0 : 12), w, h);
    } else {
      drawJarFallback(ctx, centers[i], baseY, h, COLORS[p.line_color] || '#fff');
    }
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
  });

  const cardX = 80;
  const cardY = 1090;
  const rowH = 98;
  const cardH = rowH * items.length + 56;
  ctx.fillStyle = 'rgba(12,12,16,0.62)';
  ctx.strokeStyle = 'rgba(255,255,255,0.14)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(cardX, cardY, W - cardX * 2, cardH, 44);
  ctx.fill();
  ctx.stroke();

  items.forEach((p, i) => {
    const y = cardY + 28 + i * rowH;
    const color = COLORS[p.line_color] || '#fff';
    ctx.shadowColor = color;
    ctx.shadowBlur = 18;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cardX + 62, y + 50, 11, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = `500 22px ${FONT}`;
    setLetterSpacing(ctx, '4px');
    ctx.fillText(`${(labels.typeNames[p.product_type] || p.product_type).toUpperCase()} · ${(LINE_NAMES[p.line_color] || '').toUpperCase()}`, cardX + 100, y + 30);
    setLetterSpacing(ctx, '0px');
    ctx.fillStyle = '#ffffff';
    fitFont(ctx, p.main_ingredient || '', 700, 36, 26, 520);
    ctx.fillText(p.main_ingredient || '', cardX + 100, y + 70);
    ctx.textAlign = 'right';
    ctx.fillStyle = 'rgba(255,255,255,0.62)';
    fitFont(ctx, p.effect || '', 400, 24, 18, 300);
    ctx.fillText(p.effect || '', W - cardX - 48, y + 50);
    if (i < items.length - 1) {
      ctx.fillStyle = 'rgba(255,255,255,0.07)';
      ctx.fillRect(cardX + 48, y + rowH - 2, W - cardX * 2 - 96, 2);
    }
  });

  pill(ctx, labels.volume, W / 2, 1680, 28, '#ffffff', 40);

  ctx.fillStyle = brandGradient(ctx, 200, W - 200);
  ctx.fillRect(200, 1748, W - 400, 3);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.font = `600 30px ${FONT}`;
  ctx.fillText(labels.hashtags, W / 2, 1800);
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = `400 24px ${FONT}`;
  setLetterSpacing(ctx, '4px');
  ctx.fillText(labels.site.toUpperCase(), W / 2, 1850);
  setLetterSpacing(ctx, '0px');

  return canvas;
};

export const canvasToBlob = (canvas, type = 'image/jpeg', quality = 0.92) => new Promise((resolve) => canvas.toBlob(resolve, type, quality));
